import { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Pencil,
  CheckCircle2,
  User,
  Copy,
  Download,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { editTrackingApi } from '../../services/api';

interface EditEntry {
  id: string;
  user_id: string;
  user_name: string;
  decision_id: string | null;
  has_edits: boolean;
  edit_distance: number;
  modality: string | null;
  body_part: string | null;
  exam_category: string | null;
  template_name: string | null;
  model_used: string | null;
  trigger: string;
  created_at: string;
}

interface EditDetail extends EditEntry {
  generated_report: string;
  final_report: string;
  template_id: string | null;
}

interface StatsData {
  period_days: number;
  total: number;
  edited: number;
  unedited: number;
  avg_edit_distance: number;
  top_categories: { category: string; count: number; avg_distance: number }[];
}

interface ListResponse {
  items: EditEntry[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  available_categories: string[];
}

export default function EditHistory({ days }: { days: number }) {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editFilter, setEditFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<EditDetail | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        editTrackingApi.stats(days),
        editTrackingApi.list({
          page,
          per_page: 20,
          has_edits: editFilter === '' ? undefined : editFilter === 'true',
          exam_category: categoryFilter || undefined,
          days,
        }),
      ]);
      setStats(s);
      setData(list);
    } catch (err) {
      console.error('Failed to fetch edit history:', err);
    } finally {
      setLoading(false);
    }
  }, [page, editFilter, categoryFilter, days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [editFilter, categoryFilter, days]);

  const handleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(id);
    try {
      const detail = await editTrackingApi.detail(id);
      setExpandedDetail(detail);
    } catch (err) {
      console.error('Failed to fetch edit detail:', err);
    }
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleString('en-AU', {
      hour12: false,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const stripHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Captures" value={stats.total} icon={<FileText size={20} />} color="blue" />
          <StatCard label="Reports Modified" value={stats.edited} icon={<Pencil size={20} />} color="orange" />
          <StatCard label="No Changes" value={stats.unedited} icon={<CheckCircle2 size={20} />} color="green" />
          <StatCard label="Avg Edit Distance" value={stats.avg_edit_distance} icon={<FileText size={20} />} color="purple" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Edit Status Filter */}
          <select
            value={editFilter}
            onChange={(e) => setEditFilter(e.target.value)}
            className="text-sm border border-gray-300 dark:border-surface-700 rounded-lg px-3 py-2 bg-white dark:bg-surface-800 text-gray-700 dark:text-gray-200"
          >
            <option value="">All Reports</option>
            <option value="true">Modified</option>
            <option value="false">Unmodified</option>
          </select>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-sm border border-gray-300 dark:border-surface-700 rounded-lg px-3 py-2 bg-white dark:bg-surface-800 text-gray-700 dark:text-gray-200"
          >
            <option value="">All Categories</option>
            {data?.available_categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {(editFilter || categoryFilter) && (
            <button
              onClick={() => { setEditFilter(''); setCategoryFilter(''); }}
              className="text-xs text-primary-600 hover:underline"
            >
              Clear filters
            </button>
          )}

          <span className="text-xs text-gray-400 ml-auto">
            {data?.total || 0} records
          </span>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : !data || data.items.length === 0 ? (
        <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-12 text-center">
          <p className="text-gray-400">No edit records found. Edit tracking data will appear here when reports are copied or downloaded.</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {data.items.map((entry) => (
              <div
                key={entry.id}
                className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 overflow-hidden"
              >
                {/* Card Header */}
                <div
                  className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-surface-800/50"
                  onClick={() => handleExpand(entry.id)}
                >
                  {/* Edit status badge */}
                  {entry.has_edits ? (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 text-xs font-medium min-w-[90px]">
                      <Pencil size={13} />
                      Modified
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-medium min-w-[90px]">
                      <CheckCircle2 size={13} />
                      No Change
                    </div>
                  )}

                  {/* Trigger icon */}
                  {entry.trigger === 'clipboard' ? (
                    <Copy size={14} className="text-gray-400 flex-shrink-0" />
                  ) : (
                    <Download size={14} className="text-gray-400 flex-shrink-0" />
                  )}

                  {/* Preview */}
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                    {entry.template_name || entry.exam_category || 'Unknown template'}
                  </span>

                  {/* Metadata pills */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {entry.modality && (
                      <span className="text-xs bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                        {entry.modality}
                      </span>
                    )}
                    {entry.has_edits && (
                      <span className="text-xs bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded">
                        dist: {entry.edit_distance}
                      </span>
                    )}
                    {entry.model_used && (
                      <span className="text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded">
                        {entry.model_used}
                      </span>
                    )}
                    <span className="text-xs text-gray-400 w-36 text-right">
                      {formatDate(entry.created_at)}
                    </span>
                    {expandedId === entry.id ? (
                      <ChevronUp size={14} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={14} className="text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Detail — side-by-side comparison */}
                {expandedId === entry.id && expandedDetail && expandedDetail.id === entry.id && (
                  <div className="border-t border-gray-100 dark:border-surface-800">
                    {/* Meta bar */}
                    <div className="px-4 py-2 bg-gray-50 dark:bg-surface-800/50 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User size={12} />
                        {expandedDetail.user_name}
                      </span>
                      {expandedDetail.exam_category && <span>Category: {expandedDetail.exam_category}</span>}
                      {expandedDetail.body_part && <span>Body Part: {expandedDetail.body_part}</span>}
                      {expandedDetail.template_name && <span>Template: {expandedDetail.template_name}</span>}
                      {expandedDetail.model_used && <span>Model: {expandedDetail.model_used}</span>}
                      <span>Trigger: {expandedDetail.trigger}</span>
                      {expandedDetail.has_edits && <span>Edit Distance: {expandedDetail.edit_distance}</span>}
                    </div>

                    {/* Side-by-side reports */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x divide-gray-100 dark:divide-surface-800">
                      {/* AI Original */}
                      <div className="p-4">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                          <FileText size={13} className="text-blue-500" />
                          AI Original
                        </h4>
                        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg p-3 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
                          {stripHtml(expandedDetail.generated_report)}
                        </div>
                      </div>

                      {/* Radiologist's Final */}
                      <div className="p-4">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                          <Pencil size={13} className="text-orange-500" />
                          Final Version
                        </h4>
                        <div className={`border rounded-lg p-3 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto ${
                          expandedDetail.has_edits
                            ? 'bg-orange-50 dark:bg-orange-900/10 border-orange-100 dark:border-orange-900/30'
                            : 'bg-green-50 dark:bg-green-900/10 border-green-100 dark:border-green-900/30'
                        }`}>
                          {stripHtml(expandedDetail.final_report)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-between bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 px-4 py-3">
              <span className="text-sm text-gray-500">
                Showing {(data.page - 1) * data.per_page + 1}–{Math.min(data.page * data.per_page, data.total)} of {data.total} entries
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-surface-800 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(7, data.total_pages) }, (_, i) => {
                  let pageNum: number;
                  if (data.total_pages <= 7) {
                    pageNum = i + 1;
                  } else if (page <= 4) {
                    pageNum = i + 1;
                  } else if (page >= data.total_pages - 3) {
                    pageNum = data.total_pages - 6 + i;
                  } else {
                    pageNum = page - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 rounded-md text-sm font-medium ${
                        pageNum === page
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(Math.min(data.total_pages, page + 1))}
                  disabled={page >= data.total_pages}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-surface-800 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}) {
  const colors: Record<string, string> = {
    blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400',
    orange: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400',
    green: 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400',
    purple: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400',
  };

  return (
    <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>{icon}</div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}
